import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { BaseAlertComponent } from '../../alerts/base-alert/base-alert.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { AdminUser } from '../../../dtos/admin-user';
import { UserInfo } from '../../../dtos/user-info';
import { AdminUserService } from '../../../services/admin-user.service';
import { AuthService } from '../../../services/auth.service';
import { ProfileService } from '../../../services/profile.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, BaseAlertComponent, ConfirmModalComponent],
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.scss'],
})
export class AdminUsersComponent implements OnInit {
  users: AdminUser[] = [];
  currentUser: UserInfo | null = null;
  loading = true;
  actionUserId: string | null = null;
  deleteUserCandidate: AdminUser | null = null;
  showDeleteConfirm = false;
  filterTerm = '';
  errorMessage = '';
  roleChecked = false;
  applicationDomain = '';
  savedApplicationDomain = '';
  domainLoading = false;
  domainSaving = false;
  domainErrorMessage = '';

  constructor(
    private adminUserService: AdminUserService,
    private authService: AuthService,
    private profileService: ProfileService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.profileService.getCurrentUser(true).subscribe({
      next: (user) => {
        this.currentUser = user;
        this.roleChecked = true;

        if (!this.isAdmin) {
          this.loading = false;
          return;
        }

        if (this.isSuperAdmin) {
          this.loadApplicationDomain();
        }

        this.loadUsers();
      },
      error: (error) => {
        console.error('Could not load current user', error);
        this.roleChecked = true;
        this.loading = false;
      },
    });
  }

  get isAdmin(): boolean {
    return ['ADMIN', 'SUPER_ADMIN'].includes(this.currentRole);
  }

  get isSuperAdmin(): boolean {
    return this.currentRole === 'SUPER_ADMIN';
  }

  get currentRole(): string {
    return this.currentUser?.role ?? this.authService.getUserRole();
  }

  get currentRoleLabels(): string[] {
    return this.currentRole === 'SUPER_ADMIN'
      ? ['USER', 'SUPER_ADMIN']
      : this.currentRole === 'ADMIN'
        ? ['USER', 'ADMIN']
        : ['USER'];
  }

  get currentRoleRights(): string[] {
    if (this.isSuperAdmin) {
      return [
        'View all registered users and account states.',
        'Assign or remove the Admin role for trusted users.',
        'Configure the product domain used in links sent by email.',
        'Deactivate and reactivate eligible user accounts.',
        'Transfer the Super Administrator role from your profile danger zone.',
      ];
    }

    if (this.isAdmin) {
      return [
        'View all registered users and account states.',
        'Deactivate and reactivate eligible user accounts.',
        'Review role assignments without changing administrator roles.',
      ];
    }

    return ['Use the application as a regular user.'];
  }

  get filteredUsers(): AdminUser[] {
    const term = this.filterTerm.trim().toLowerCase();
    if (!term) {
      return this.users;
    }

    return this.users.filter((user) =>
      [user.firstName, user.lastName, user.email, user.affiliation, this.formatRoles(user)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }

  loadUsers(): void {
    this.loading = true;
    this.errorMessage = '';

    this.adminUserService.getUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.loading = false;
      },
      error: (error) => {
        console.error('Could not load users', error);
        this.errorMessage = this.extractErrorMessage(error, 'Could not load registered users.');
        this.loading = false;
      },
    });
  }

  loadApplicationDomain(): void {
    if (!this.isSuperAdmin) {
      return;
    }

    this.domainLoading = true;
    this.domainErrorMessage = '';

    this.adminUserService.getApplicationDomain().subscribe({
      next: (settings) => {
        this.applicationDomain = settings.applicationDomain;
        this.savedApplicationDomain = settings.applicationDomain;
        this.domainLoading = false;
      },
      error: (error) => {
        console.error('Could not load application domain', error);
        this.domainErrorMessage = this.extractErrorMessage(
          error,
          'Could not load the configured application domain.',
        );
        this.domainLoading = false;
      },
    });
  }

  saveApplicationDomain(): void {
    if (!this.isSuperAdmin || this.domainSaving) {
      return;
    }

    const normalizedDomain = this.normalizeApplicationDomain(this.applicationDomain);
    if (!normalizedDomain || !this.isValidApplicationDomain(normalizedDomain)) {
      this.domainErrorMessage =
        'Enter a valid http or https URL, for example http://localhost:4200.';
      return;
    }

    this.domainSaving = true;
    this.domainErrorMessage = '';

    this.adminUserService.updateApplicationDomain(normalizedDomain).subscribe({
      next: (settings) => {
        this.applicationDomain = settings.applicationDomain;
        this.savedApplicationDomain = settings.applicationDomain;
        this.domainSaving = false;
        this.toastService.success(
          'Application domain saved',
          'Email links now use the configured domain.',
        );
      },
      error: (error) => {
        console.error('Could not save application domain', error);
        this.domainSaving = false;
        this.domainErrorMessage = this.extractErrorMessage(
          error,
          'Could not save the application domain.',
        );
        this.toastService.error('Domain update failed', this.domainErrorMessage);
      },
    });
  }

  updateFilter(value: string): void {
    this.filterTerm = value;
  }

  applicationDomainChanged(): boolean {
    return this.normalizeApplicationDomain(this.applicationDomain) !== this.savedApplicationDomain;
  }

  hasAdminRole(user: AdminUser): boolean {
    return user.roles.some((role) => role === 'ROLE_ADMIN' || role === 'ADMIN');
  }

  hasSuperAdminRole(user: AdminUser): boolean {
    return user.roles.some((role) => role === 'ROLE_SUPER_ADMIN' || role === 'SUPER_ADMIN');
  }

  isCurrentUser(user: AdminUser): boolean {
    return (
      (!!this.currentUser?.id && String(this.currentUser.id) === String(user.id)) ||
      (!!this.currentUser?.email &&
        this.currentUser.email.toLowerCase() === user.email.toLowerCase())
    );
  }

  canDeactivateUser(user: AdminUser): boolean {
    return (
      user.active &&
      !this.actionUserId &&
      !this.isCurrentUser(user) &&
      !this.hasSuperAdminRole(user)
    );
  }

  deactivateDisabledReason(user: AdminUser): string {
    if (this.hasSuperAdminRole(user)) {
      return 'Super Administrator accounts cannot be deactivated here';
    }
    if (this.isCurrentUser(user)) {
      return 'Use your profile page to deactivate your own account';
    }
    return 'Deactivate user account';
  }

  formatName(user: AdminUser): string {
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name || user.email;
  }

  formatRoles(user: AdminUser): string {
    const roles = user.roles.map((role) => role.replace(/^ROLE_/, ''));
    return roles.length ? roles.join(', ') : 'USER';
  }

  assignAdmin(user: AdminUser): void {
    if (this.actionUserId || this.hasAdminRole(user) || !this.isSuperAdmin) {
      return;
    }

    this.actionUserId = user.id;
    this.adminUserService.assignAdminRole(user.id).subscribe({
      next: (updatedUser) => {
        this.replaceUser(
          updatedUser ?? { ...user, roles: this.withRole(user.roles, 'ROLE_ADMIN') },
        );
        this.actionUserId = null;
        this.toastService.success(
          'Admin role assigned',
          `${this.formatName(user)} is now an admin.`,
        );
      },
      error: (error) => {
        console.error('Could not assign admin role', error);
        this.actionUserId = null;
        this.errorMessage = this.extractErrorMessage(error, 'Could not assign the admin role.');
        this.toastService.error('Role update failed', this.errorMessage);
      },
    });
  }

  removeAdmin(user: AdminUser): void {
    if (
      this.actionUserId ||
      !this.hasAdminRole(user) ||
      !this.isSuperAdmin ||
      this.isCurrentUser(user) ||
      this.hasSuperAdminRole(user)
    ) {
      return;
    }

    this.actionUserId = user.id;
    this.adminUserService.removeAdminRole(user.id).subscribe({
      next: (updatedUser) => {
        this.replaceUser(
          updatedUser ?? { ...user, roles: this.withoutRole(user.roles, 'ROLE_ADMIN') },
        );
        this.actionUserId = null;
        this.toastService.success(
          'Admin role removed',
          `${this.formatName(user)} is no longer an admin.`,
        );
      },
      error: (error) => {
        console.error('Could not remove admin role', error);
        this.actionUserId = null;
        this.errorMessage = this.extractErrorMessage(error, 'Could not remove the admin role.');
        this.toastService.error('Role update failed', this.errorMessage);
      },
    });
  }

  askDeleteUser(user: AdminUser): void {
    if (!this.canDeactivateUser(user)) {
      return;
    }

    this.deleteUserCandidate = user;
    this.showDeleteConfirm = true;
  }

  cancelDeleteUser(): void {
    this.showDeleteConfirm = false;
    this.deleteUserCandidate = null;
  }

  confirmDeleteUser(): void {
    if (!this.deleteUserCandidate || this.actionUserId) {
      return;
    }

    const user = this.deleteUserCandidate;
    this.actionUserId = user.id;
    this.adminUserService.deleteUser(user.id).subscribe({
      next: () => {
        this.replaceUser({ ...user, active: false, deactivatedAt: new Date().toISOString() });
        this.actionUserId = null;
        this.cancelDeleteUser();
        this.toastService.success(
          'User deactivated',
          `${this.formatName(user)} has been deactivated.`,
        );
      },
      error: (error) => {
        console.error('Could not delete user', error);
        this.actionUserId = null;
        this.showDeleteConfirm = false;
        this.errorMessage = this.extractErrorMessage(error, 'Could not delete the user account.');
        this.toastService.error('Delete failed', this.errorMessage);
      },
    });
  }

  reactivateUser(user: AdminUser): void {
    if (this.actionUserId || user.active) {
      return;
    }

    this.actionUserId = user.id;
    this.adminUserService.reactivateUser(user.id).subscribe({
      next: (updatedUser) => {
        this.replaceUser(updatedUser ?? { ...user, active: true, deactivatedAt: null });
        this.actionUserId = null;
        this.toastService.success(
          'User activated',
          `${this.formatName(user)} can access the system again.`,
        );
      },
      error: (error) => {
        console.error('Could not reactivate user', error);
        this.actionUserId = null;
        this.errorMessage = this.extractErrorMessage(error, 'Could not activate the user account.');
        this.toastService.error('Activation failed', this.errorMessage);
      },
    });
  }

  private replaceUser(updatedUser: AdminUser): void {
    this.users = this.users.map((user) => (user.id === updatedUser.id ? updatedUser : user));
  }

  private withRole(roles: string[], role: string): string[] {
    return roles.includes(role) ? roles : [...roles, role];
  }

  private withoutRole(roles: string[], role: string): string[] {
    return roles.filter((item) => item !== role && item !== role.replace(/^ROLE_/, ''));
  }

  private extractErrorMessage(error: any, fallback: string): string {
    if (error?.error?.detail) {
      return error.error.detail;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (typeof error?.error === 'string') {
      return error.error;
    }

    if (error?.message) {
      return error.message;
    }

    return fallback;
  }

  private normalizeApplicationDomain(value: string): string {
    return value.trim().replace(/\/+$/, '');
  }

  private isValidApplicationDomain(value: string): boolean {
    // Mirrors the backend: a bare trailing '?' or '#' is rejected there too, but parses
    // to an empty (falsy) url.search/url.hash here, so check the raw string instead.
    if (value.includes('?') || value.includes('#')) {
      return false;
    }
    try {
      const url = new URL(value);
      return ['http:', 'https:'].includes(url.protocol) && !!url.hostname;
    } catch {
      return false;
    }
  }
}
